import { Producto } from '../types';

export class ProductExportService {
  /**
   * Export products to CSV with all required fields
   */
  static exportToCSV(
    productos: Producto[],
    filename: string,
    options?: { includeCostColumns?: boolean }
  ) {
    if (productos.length === 0) {
      alert('No hay productos para exportar');
      return;
    }

    const includeCostColumns = options?.includeCostColumns !== false;

    // CSV Headers
    const headers = includeCostColumns
      ? [
          'SKU',
          'Nombre',
          'Descripción',
          'Cantidad (Stock)',
          'Precio de Compra (Costo)',
          'Precio de Venta',
          'Margen (%)',
          'Utilidad (%)',
          'Categoría',
          'Estado'
        ]
      : [
          'SKU',
          'Nombre',
          'Descripción',
          'Cantidad (Stock)',
          'Precio de Venta',
          'Categoría',
          'Estado'
        ];

    // Convert products to CSV rows
    const rows = productos.map(producto => {
      const precioCompra = producto.precioCompra || 0;
      const precioVenta = producto.precioVenta || 0;
      
      // Calculate margin: (Precio Venta - Precio Compra) / Precio Venta * 100
      const margen = precioVenta > 0 
        ? ((precioVenta - precioCompra) / precioVenta * 100).toFixed(2)
        : '0.00';
      
      // Calculate utilidad (profit): (Precio Venta - Precio Compra) / Precio Compra * 100
      const utilidad = precioCompra > 0 
        ? ((precioVenta - precioCompra) / precioCompra * 100).toFixed(2)
        : '0.00';

      const base = [
        producto.sku || 'N/A',
        `"${producto.nombre}"`, // Quotes for names with commas
        `"${(producto.descripcion || '').replace(/"/g, '""')}"`, // Escape quotes
        producto.stock || 0,
      ];
      if (includeCostColumns) {
        return [
          ...base,
          precioCompra.toFixed(2),
          precioVenta.toFixed(2),
          margen,
          utilidad,
          producto.categoria || 'Sin categoría',
          producto.estado || 'ACTIVO'
        ];
      }
      return [
        ...base,
        precioVenta.toFixed(2),
        producto.categoria || 'Sin categoría',
        producto.estado || 'ACTIVO'
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`✅ Exported ${productos.length} products to CSV`);
  }

  /**
   * Export products to Excel format (actually CSV with .xlsx extension for simplicity)
   */
  static exportToExcel(
    productos: Producto[],
    filename: string,
    options?: { includeCostColumns?: boolean }
  ) {
    // For now, use CSV format but with .xlsx extension
    // In the future, we can add a proper Excel library like xlsx
    this.exportToCSV(productos, filename.replace('.xlsx', '.csv'), options);
  }

  /**
   * Calculate summary statistics for products
   */
  static getProductStats(productos: Producto[]) {
    const totalProductos = productos.length;
    const totalStock = productos.reduce((sum, p) => sum + (p.stock || 0), 0);
    const valorInventarioCompra = productos.reduce((sum, p) => 
      sum + ((p.precioCompra || 0) * (p.stock || 0)), 0
    );
    const valorInventarioVenta = productos.reduce((sum, p) => 
      sum + ((p.precioVenta || 0) * (p.stock || 0)), 0
    );
    const margenPromedio = productos.length > 0
      ? productos.reduce((sum, p) => {
          const precioCompra = p.precioCompra || 0;
          const precioVenta = p.precioVenta || 0;
          const margen = precioVenta > 0 ? ((precioVenta - precioCompra) / precioVenta * 100) : 0;
          return sum + margen;
        }, 0) / productos.length
      : 0;

    return {
      totalProductos,
      totalStock,
      valorInventarioCompra,
      valorInventarioVenta,
      margenPromedio: margenPromedio.toFixed(2),
      utilidadPotencial: (valorInventarioVenta - valorInventarioCompra).toFixed(2)
    };
  }
}

